import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import { Badge, Banner, EmptyState, PageLoading, SearchInput, Select } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';

import OrderDetail from './OrderDetail';
import {
  STATUS_LABEL, STATUS_TONE, daysAgo, money, moneyShort, today,
} from './orderMeta';

import type {
  CompanySettings, OpenOrders, OrderExport, OrderList, OrderStatus,
  SalesByRep, TopProduct,
} from '../../types';

type Tab = 'queue' | 'all' | 'reports';

/**
 * Orders, from the office's side.
 *
 * The screen opens on **the queue** rather than a list of everything,
 * because the office's job here is not to browse orders — it is to work
 * through the ones nobody has dealt with yet. A list sorted by date
 * buries a six-day-old pending order under everything booked this
 * morning, which is exactly the one that needed attention.
 */
export default function OrdersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const canAct = can('manager', 'company_admin', 'platform_admin');

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [tab, setTab] = useState<Tab>('queue');
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get<CompanySettings>('/settings')
      .then(setSettings)
      .catch((err) => toast.error(
        err instanceof ApiClientError ? err.message : 'Could not load settings.',
      ));
  }, [toast]);

  const refresh = useCallback(() => setReloadKey((n) => n + 1), []);

  if (!settings) {
    return (
      <>
        <PageHeader title="Orders" />
        <div className="page"><PageLoading /></div>
      </>
    );
  }

  if (!settings.ordersEnabled) {
    return (
      <>
        <PageHeader title="Orders" />
        <div className="page">
          <div className="card">
            <EmptyState
              icon="list"
              title="Order booking is switched off"
              body="Your reps do not see an order button, and nothing here will fill up.
                    Turn it on in Settings when your products and prices are ready —
                    a rep who opens an empty catalogue in front of a shopkeeper is worse
                    than one who has no button at all."
              action={(
                <a className="btn btn-primary" href="/settings">Open settings</a>
              )}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Orders"
        subtitle="What your team booked, and what still needs doing about it."
      />

      <div className="page col gap-16">
        <div className="tabs">
          <button type="button" className={`tab${tab === 'queue' ? ' active' : ''}`}
            onClick={() => setTab('queue')}>
            Queue
          </button>
          <button type="button" className={`tab${tab === 'all' ? ' active' : ''}`}
            onClick={() => setTab('all')}>
            All orders
          </button>
          <button type="button" className={`tab${tab === 'reports' ? ' active' : ''}`}
            onClick={() => setTab('reports')}>
            Reports
          </button>
        </div>

        {tab === 'queue' ? <QueueTab key={`q${reloadKey}`} onOpen={setOpenOrder} /> : null}
        {tab === 'all' ? <AllTab key={`a${reloadKey}`} onOpen={setOpenOrder} /> : null}
        {tab === 'reports' ? <ReportsTab key={`r${reloadKey}`} /> : null}
      </div>

      {openOrder ? (
        <OrderDetail
          orderId={openOrder}
          canAct={canAct}
          onClose={() => setOpenOrder(null)}
          onChanged={refresh}
        />
      ) : null}
    </>
  );
}

// =====================================================================

function QueueTab({ onOpen }: { onOpen: (id: string) => void }) {
  const toast = useToast();
  const [open, setOpen] = useState<OpenOrders | null>(null);
  const [status, setStatus] = useState<OrderStatus>('pending');
  const [list, setList] = useState<OrderList | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<OpenOrders>('/orders/reports/open').then(setOpen).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<OrderList>('/orders', { status, limit: 100 })
      .then((data) => { if (!cancelled) setList(data); })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load orders.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, toast]);

  const buckets: { status: OrderStatus; label: string; hint: string }[] = [
    { status: 'pending', label: 'To confirm', hint: 'Nobody has looked at these yet' },
    { status: 'confirmed', label: 'To dispatch', hint: 'Accepted, not yet sent' },
    { status: 'dispatched', label: 'On the way', hint: 'Sent, not yet delivered' },
  ];

  return (
    <div className="col gap-16">
      <div className="stat-row">
        {buckets.map((bucket) => {
          const data = open?.[bucket.status as keyof OpenOrders];
          const stale = (data?.daysWaiting ?? 0) >= 3 && (data?.orders ?? 0) > 0;
          return (
            <button
              key={bucket.status}
              type="button"
              className={`stat stat-button${status === bucket.status ? ' active' : ''}`}
              onClick={() => setStatus(bucket.status)}
            >
              <div className="stat-icon"><Icon name="list" size={18} /></div>
              <div className="grow">
                <div className="stat-label">{bucket.label}</div>
                <div className="stat-value">{data?.orders ?? '—'}</div>
                <div className="small faint">
                  {data && data.orders > 0
                    ? `${moneyShort(data.value)}${stale
                      ? ` · oldest ${data.daysWaiting} days` : ''}`
                    : bucket.hint}
                </div>
              </div>
              {stale ? <Icon name="alert" size={17} /> : null}
            </button>
          );
        })}
      </div>

      {loading ? <PageLoading /> : !list || list.orders.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="check"
            title={`Nothing ${status === 'pending' ? 'waiting to be confirmed'
              : status === 'confirmed' ? 'waiting to be dispatched' : 'in transit'}`}
            body="This queue is clear."
          />
        </div>
      ) : (
        <OrderTable orders={list.orders} onOpen={onOpen} showAge />
      )}
    </div>
  );
}

// =====================================================================

function AllTab({ onOpen }: { onOpen: (id: string) => void }) {
  const toast = useToast();
  const { company } = useAuth();
  const tz = company?.timezone;

  const [list, setList] = useState<OrderList | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState(() => daysAgo(29, tz));
  const [to, setTo] = useState(() => today(tz));

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      api.get<OrderList>('/orders', {
        search: search || undefined,
        status: status || undefined,
        from, to, limit: 200,
      })
        .then((data) => { if (!cancelled) setList(data); })
        .catch((err) => {
          if (!cancelled) {
            toast.error(err instanceof ApiClientError ? err.message : 'Could not load orders.');
          }
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, search ? 250 : 0);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [search, status, from, to, toast]);

  const value = (list?.orders ?? [])
    .filter((o) => o.status !== 'cancelled')
    .reduce((n, o) => n + o.totalAmount, 0);

  return (
    <div className="col gap-16">
      <div className="toolbar between">
        <div className="row gap-8">
          <SearchInput value={search} onChange={setSearch} placeholder="Order no. or customer" />
          <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
            <option value="">Any status</option>
            {(Object.keys(STATUS_LABEL) as OrderStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </Select>
          <input type="date" className="input" value={from} max={to}
            onChange={(e) => setFrom(e.target.value)} aria-label="From" />
          <span className="faint small">to</span>
          <input type="date" className="input" value={to} min={from} max={today(tz)}
            onChange={(e) => setTo(e.target.value)} aria-label="To" />
        </div>
        {list ? (
          <span className="small faint">
            {list.orders.length} order{list.orders.length === 1 ? '' : 's'} · {moneyShort(value)}
          </span>
        ) : null}
      </div>

      {loading ? <PageLoading /> : !list || list.orders.length === 0 ? (
        <div className="card">
          <EmptyState icon="list" title="No orders in this range" />
        </div>
      ) : (
        <OrderTable orders={list.orders} onOpen={onOpen} />
      )}
    </div>
  );
}

// =====================================================================

function OrderTable({
  orders, onOpen, showAge = false,
}: { orders: OrderList['orders']; onOpen: (id: string) => void; showAge?: boolean }) {
  return (
    <div className="card">
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Booked by</th>
              <th>Date</th>
              {showAge ? <th className="num">Waiting</th> : null}
              <th className="num">Lines</th>
              <th className="num">Value</th>
              <th>Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const days = Math.floor(
                (Date.now() - new Date(order.orderDate).getTime()) / 86400000,
              );
              return (
                <tr key={order.id} className="clickable" onClick={() => onOpen(order.id)}>
                  <td className="mono small nowrap">{order.orderNo}</td>
                  <td className="primary">
                    {order.customer?.name ?? '—'}
                    {order.customer?.route ? (
                      <div className="small faint">{order.customer.route}</div>
                    ) : null}
                  </td>
                  <td className="nowrap">{order.user?.name ?? '—'}</td>
                  <td className="nowrap">{order.orderDate}</td>
                  {showAge ? (
                    <td className="num">
                      {days >= 3
                        ? <Badge tone="warning">{days} days</Badge>
                        : <span className="faint">{days === 0 ? 'Today' : `${days}d`}</span>}
                    </td>
                  ) : null}
                  <td className="num">{order.itemCount}</td>
                  <td className="num primary">{money(order.totalAmount)}</td>
                  <td>
                    {order.orderType === 'primary'
                      ? <Badge tone="info">Primary</Badge>
                      : <span className="faint small">Secondary</span>}
                  </td>
                  <td>
                    <Badge tone={STATUS_TONE[order.status]}>{STATUS_LABEL[order.status]}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =====================================================================

function ReportsTab() {
  const toast = useToast();
  const { company } = useAuth();
  const tz = company?.timezone;

  const [from, setFrom] = useState(() => daysAgo(29, tz));
  const [to, setTo] = useState(() => today(tz));
  const [byRep, setByRep] = useState<SalesByRep | null>(null);
  const [products, setProducts] = useState<TopProduct[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get<SalesByRep>('/orders/reports/by-rep', { from, to }),
      api.get<TopProduct[]>('/orders/reports/top-products', { from, to, limit: 20 }),
    ])
      .then(([reps, top]) => {
        if (cancelled) return;
        setByRep(reps);
        setProducts(top);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load reports.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, toast]);

  /**
   * The export is a CSV built in the browser from what the API returns.
   * One row per order LINE, with the snapshot fields, because that is
   * the shape an accounts package can actually read — an order-level
   * export makes whoever receives it re-derive the lines.
   */
  async function exportCsv() {
    setExporting(true);
    try {
      const data = await api.get<OrderExport>('/orders/reports/export', { from, to });
      if (!data.rowCount) {
        toast.error('There is nothing to export in this range.');
        return;
      }

      const escape = (value: unknown) => {
        const text = value == null ? '' : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
      };
      const csv = [
        data.columns.join(','),
        ...data.rows.map((row) => data.columns.map((c) => escape(row[c])).join(',')),
      ].join('\n');

      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `orders-${from}-to-${to}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`${data.rowCount} lines exported.`);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not export that.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="col gap-16">
      <div className="toolbar between">
        <div className="row gap-8">
          <input type="date" className="input" value={from} max={to}
            onChange={(e) => setFrom(e.target.value)} aria-label="From" />
          <span className="faint small">to</span>
          <input type="date" className="input" value={to} min={from} max={today(tz)}
            onChange={(e) => setTo(e.target.value)} aria-label="To" />
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => { setFrom(daysAgo(29, tz)); setTo(today(tz)); }}>
            Last 30 days
          </button>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" disabled={exporting}
          onClick={() => void exportCsv()}>
          <Icon name="history" size={15} /> Export lines (CSV)
        </button>
      </div>

      {loading ? <PageLoading /> : (
        <>
          {byRep ? (
            <>
              <div className="stat-row">
                <Stat label="Orders" value={String(byRep.totals.orders)} icon="list" />
                <Stat label="Value" value={moneyShort(byRep.totals.value)} icon="check" />
                <Stat
                  label="Cancelled"
                  value={String(byRep.totals.cancelled)}
                  icon="alert"
                  tone={byRep.totals.cancelled > 0 ? 'warning' : undefined}
                />
              </div>

              <div className="card">
                <div className="card-head">
                  <h3>By rep</h3>
                  <span className="small faint">Cancelled orders are counted, not valued</span>
                </div>
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Rep</th>
                        <th className="num">Orders</th>
                        <th className="num">Shops</th>
                        <th className="num">Average</th>
                        <th className="num">Cancelled</th>
                        <th className="num">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byRep.reps.map((rep) => (
                        <tr key={rep.userId}>
                          <td className="primary">{rep.name}</td>
                          <td className="num">{rep.orders}</td>
                          <td className="num">{rep.customers}</td>
                          <td className="num">{rep.orders ? money(rep.averageOrder) : '—'}</td>
                          <td className="num">
                            {rep.cancelled > 0
                              ? <Badge tone="warning">{rep.cancelled}</Badge>
                              : <span className="faint">0</span>}
                          </td>
                          <td className="num primary">{money(rep.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : null}

          {products?.length ? (
            <div className="card">
              <div className="card-head">
                <h3>What is selling</h3>
                <span className="small faint">By value</span>
              </div>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="num">Quantity</th>
                      <th className="num">Orders</th>
                      <th className="num">Shops</th>
                      <th className="num">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.productId ?? product.code}>
                        <td className="primary">
                          {product.name}
                          <div className="small faint mono">{product.code}</div>
                        </td>
                        <td className="num nowrap">
                          {Number(product.quantity.toFixed(3))} {product.unit}
                        </td>
                        <td className="num">{product.orders}</td>
                        <td className="num">{product.customers}</td>
                        <td className="num primary">{money(product.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <Banner kind="info">
              Nothing was ordered in this range, so there is nothing to rank.
            </Banner>
          )}
        </>
      )}
    </div>
  );
}

function Stat({
  label, value, icon, tone,
}: { label: string; value: string; icon: 'list' | 'check' | 'alert'; tone?: 'warning' }) {
  return (
    <div className="stat">
      <div className="stat-icon"><Icon name={icon} size={18} /></div>
      <div>
        <div className="stat-label">{label}</div>
        <div className={`stat-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      </div>
    </div>
  );
}
